import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Alert,
  Divider,
  styled,
  Button,
  CircularProgress,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import { useLocation, useNavigate } from "react-router-dom";
import UnifiedPaymentButton from "../../components/payment/UnifiedPaymentButton";
import hesabPayIcon from "../../assets/images/hesabpay.png";
import { CartContext } from "../../contexts/CartContext";

// Styled Components
const CheckoutContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: "100vh",
  padding: theme.spacing(4, 0),
}));

const CheckoutPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[2],
  },
}));

const SecurityBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(3),
  "& .MuiSvgIcon-root": {
    color: "white",
  },
  "& .MuiTypography-root": {
    color: "white",
    fontWeight: 500,
  },
}));

const OrderSummaryBox = styled(Paper)(({ theme }) => ({
  position: "sticky",
  top: 24,
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
}));

const PaymentButton = styled(Button)(({ theme }) => ({
  width: "100%",
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.3s ease",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  textTransform: "none",
  fontSize: "1rem",
  fontWeight: 400,
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
    transform: "translateY(-2px)",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

const PaymentLogo = styled("img")(({ theme }) => ({
  height: 28,
  marginLeft: "auto",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const OrDivider = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  textAlign: "center",
  margin: theme.spacing(2, 0),
  "&::before, &::after": {
    content: '""',
    flex: 1,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  "& .MuiTypography-root": {
    padding: theme.spacing(0, 2),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}));

const UnifiedCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [paymentError, setPaymentError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get payment type and items from location state
  const { type, items } = location.state || { type: "BOOK", items: cartItems };

  // Redirect if no items
  useEffect(() => {
    if (!items || (Array.isArray(items) && items.length === 0)) {
      navigate("/");
    }
  }, [items, navigate]);

  const getTitle = () => {
    switch (type) {
      case "MAGAZINE":
        return "Complete Your Magazine Purchase";
      case "SUBSCRIPTION":
        return "Complete Your Subscription";
      default:
        return "Complete Your Purchase";
    }
  };

  const getSubtitle = () => {
    switch (type) {
      case "MAGAZINE":
        return "You're just one step away from accessing this magazine";
      case "SUBSCRIPTION":
        return "You're just one step away from accessing our entire library";
      default:
        return "You're just one step away from accessing your books";
    }
  };

  const handleHesabPayment = async () => {
    try {
      setIsLoading(true);
      setPaymentError(null);

      const payload = {
        payment_type: type,
        items: Array.isArray(items)
          ? items.map((item) => ({
            id: item.id.toString(),
            quantity: item.quantity || 1,
          }))
          : [{ id: items.id.toString(), quantity: 1 }],
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/hesabpay/create_session/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Payment initialization failed");
      }

      if (!data.checkout_url) {
        throw new Error("Invalid payment gateway response");
      }

      // Redirect to HesabPay checkout URL in the same tab
      window.location.href = data.checkout_url;

    } catch (error) {
      setPaymentError(error.message);
      setIsLoading(false);
    }
  };

  const calculateTotal = () => {
    if (Array.isArray(items)) {
      return items.reduce(
        (total, item) => total + parseFloat(item.price) * (item.quantity || 1),
        0,
      );
    }
    return parseFloat(items.price);
  };

  if (!items || (Array.isArray(items) && items.length === 0)) {
    return null; // Already redirected by useEffect
  }

  return (
    <CheckoutContainer>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight="600">
            {getTitle()}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getSubtitle()}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Side - Payment Details */}
          <Grid item xs={12} md={7}>
            <SecurityBadge>
              <SecurityIcon />
              <Typography variant="body2">
                Your payment is protected with industry-standard encryption
              </Typography>
            </SecurityBadge>

            {paymentError && (
              <Alert
                severity="error"
                onClose={() => setPaymentError(null)}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: (theme) =>
                    `0 2px 8px ${theme.palette.error.main}20`,
                }}
              >
                {paymentError}
              </Alert>
            )}

            <CheckoutPaper elevation={0}>
              <Typography variant="h6" sx={{ mb: 4, fontWeight: 500 }}>
                Select Payment Method
              </Typography>

              {/* HesabPay Button */}
              <PaymentButton
                onClick={handleHesabPayment}
                disabled={isLoading}
                sx={{
                  mb: 2,
                  "&:not(:disabled):hover": {
                    borderColor: "primary.main",
                    bgcolor: "primary.main",
                    color: "common.white",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <Typography component="span">
                        Pay with HesabPay
                      </Typography>
                      <PaymentLogo
                        src={hesabPayIcon}
                        alt="HesabPay"
                        loading="lazy"
                        aria-hidden="true"
                      />
                    </>
                  )}
                </Box>
              </PaymentButton>

              <OrDivider>
                <Typography variant="body2">or</Typography>
              </OrDivider>

              {/* PayPal Button */}
              <UnifiedPaymentButton
                paymentType={type}
                items={Array.isArray(items) ? items : [items]}
                clearCart={type === "BOOK" ? clearCart : undefined}
              />
            </CheckoutPaper>
          </Grid>

          {/* Right Side - Order Summary */}
          <Grid item xs={12} md={5}>
            <OrderSummaryBox>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Order Summary
                </Typography>
                {Array.isArray(items) ? (
                  items.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography>
                        {item.title}
                        {item.quantity > 1 && ` (x${item.quantity})`}
                      </Typography>
                      <Typography>
                        $
                        {(
                          parseFloat(item.price) * (item.quantity || 1)
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography>{items.title || items.name}</Typography>
                    <Typography>
                      ${parseFloat(items.price).toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                  fontWeight: "bold",
                }}
              >
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="primary">
                  ${calculateTotal().toFixed(2)}
                </Typography>
              </Box>

              <Alert
                severity="info"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: "white",
                  "& .MuiAlert-icon": {
                    color: "white",
                  },
                }}
                variant="filled"
              >
                You'll get immediate access after successful payment
              </Alert>
            </OrderSummaryBox>
          </Grid>
        </Grid>
      </Container>
    </CheckoutContainer>
  );
};

export default UnifiedCheckout;
